import classNames from "classnames";
import { add, lessThanOrEqual } from "dinero.js";
import React from "react";
import { Image } from "tsi-common-react/src/components/Image/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import {
    AddToCartType,
    BundleGroupTypes,
} from "tsi-common-react/src/constants";
import {
    IConcreteBundle,
    IProduct,
} from "tsi-common-react/src/models/catalogue";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { trackAddToBasketEvent } from "tsi-common-react/src/utils/analytics";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import {
    type Dinero,
    getDinero,
    getMonthlyPrice,
} from "tsi-common-react/src/utils/money";
import { jt, t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { IBasketLineSuggestionProps } from "../BasketLineSuggestion.interfaces";

const scrollToFinePrint = (event: React.MouseEvent<HTMLElement>): void => {
    if (event) {
        event.preventDefault();
    }

    const document = getDocument();
    const finePrint = document?.querySelector("#basket-lines__fine-print");
    finePrint?.scrollIntoView({ behavior: "smooth" });
};

const buildImage = (product: IProduct): React.ReactNode => {
    // Use the product image if one exists else try the parent image
    let image = product.images.slice().shift();
    if (!image && product.parent) {
        image = product.parent.images.slice().shift();
    }
    return image ? (
        <Link
            href={product.parent?.link || product.link}
            title={t`Learn more about ${product.title}`}
        >
            <Image
                alt={product.title}
                src={image.original}
                className="basket-line__graphic__image"
            />
        </Link>
    ) : null;
};

const buildOptions = (product: IProduct): React.ReactNode => {
    const attributes = product.attributes;
    const optionKeys = attributes.product_options?.value || [];
    if (optionKeys.length <= 0) {
        return null;
    }

    const options = optionKeys.map((key) => {
        const attr = attributes[key];
        if (!attr) {
            return null;
        }
        return (
            <li key={key} className="basket-suggestion__details__option">
                <span>{attr.name}</span>: <strong>{attr.value}</strong>
            </li>
        );
    });

    return (
        <div className="basket-suggestion__details__options">
            <ul>{options}</ul>
        </div>
    );
};

const calcPlanMonths = (total: Dinero): number | null => {
    const plans = listFinancingPlans();
    const availablePlans = plans.filter((plan) => {
        return lessThanOrEqual(plan.threshold, total);
    });
    const bestPlan = availablePlans[availablePlans.length - 1];
    return bestPlan ? isoMonths.unwrap(bestPlan.length) : null;
};

const buildHeadline = (bundles: readonly IConcreteBundle[]): string => {
    let headline = t`FORGETTING SOMETHING?`;
    for (const bundle of bundles) {
        const bundle_type = bundle.bundle_group.bundle_type;
        if (
            (bundle_type === BundleGroupTypes.DEFAULT ||
                bundle_type === BundleGroupTypes.IN_BASKET_ADD_ON) &&
            bundle.bundle_group.headline
        ) {
            headline = bundle.bundle_group.headline;
            break;
        }
    }
    return headline;
};

export const BasketLineSuggestionA = (
    props: IBasketLineSuggestionProps,
): React.ReactNode => {
    const handleAddToBasket = async (): Promise<void> => {
        await props.addBasketLine(props.product.url, 1);
        trackAddToBasketEvent(
            props.product,
            1,
            AddToCartType.IN_CART_UP_CROSS_SELL,
        );
    };

    const buildAddToBasketButton = (): React.ReactNode => {
        let addToCartLabel: string = t`Add to cart`;
        let isPreorder = false;
        if (
            props.product.attributes &&
            props.product.attributes.preorder &&
            props.product.attributes.preorder.value
        ) {
            addToCartLabel = t`Pre-Order`;
            isPreorder = true;
        }

        const buttonClasses = classNames({
            "button": true,
            "button--secondary": isPreorder,
        });

        return (
            <button className={buttonClasses} onClick={handleAddToBasket}>
                {addToCartLabel}
            </button>
        );
    };

    const line = props.line;
    const lineProduct = line.product;
    const lineRootProduct = lineProduct.parent || lineProduct;

    const product = props.product;
    const rootProduct = product.parent || product;

    let financing: React.ReactNode = null;
    if (product.price.enable_per_month_pricing) {
        // Calculate Suggested plan based on (Cart total + Suggestion Product total) / (Highest plan that total qualifies for)
        const basketTotalPrice = getDinero(props.basketTotal);
        const extraProductPrice = getDinero(
            props.product.price.cosmetic_excl_tax || "0.00",
        );
        const computedTotalPrice = add(basketTotalPrice, extraProductPrice);
        const planMonths = calcPlanMonths(computedTotalPrice);
        if (planMonths) {
            const computedMonthlyPrice = getMonthlyPrice(
                getDinero(props.product.price.cosmetic_excl_tax || "0.00"),
                planMonths,
            );
            financing = (
                <p>
                    <button
                        onClick={scrollToFinePrint}
                        className="basket-suggestion__financing-button"
                    >
                        {(() => {
                            const monthlyPrice = (
                                <span
                                    key="price"
                                    className="basket-suggestion__financing--underline"
                                >
                                    {format.money(computedMonthlyPrice)}
                                </span>
                            );
                            const months = (
                                <span
                                    key="months"
                                    className="basket-suggestion__financing--underline"
                                >
                                    {planMonths}
                                </span>
                            );
                            const sup1 = <sup key="sup1">1</sup>;
                            const sup2 = <sup key="sup2">2</sup>;
                            return jt`or ${monthlyPrice}/mo.${sup2} for ${months} months${sup1}`;
                        })()}
                    </button>
                </p>
            );
        }
    }
    const bundles = line.bundles;
    const headline = buildHeadline(bundles);

    return (
        <div
            role="region"
            aria-label={t`Upsell Carousel`}
            className="basket-suggestion"
        >
            <div className="basket-suggestion__row basket-suggestion__row--shaded">
                <h3 dangerouslySetInnerHTML={{ __html: headline }} />
            </div>
            <div className="basket-suggestion__row">
                <div className="basket-suggestion__graphic">
                    {buildImage(product)}
                </div>
                <div className="basket-suggestion__details">
                    <h5>{t`BEST SELLER for ${lineRootProduct.title}`}</h5>
                    <h4 className="basket-suggestion__details-title">
                        <Link href={rootProduct.link}>{rootProduct.title}</Link>
                    </h4>
                    {buildOptions(product)}
                </div>
                <div className="basket-suggestion__price">
                    <p>{format.money(product.price.cosmetic_excl_tax)}</p>
                    <div className="basket-suggestion__financing">
                        {financing}
                    </div>
                    {buildAddToBasketButton()}
                </div>
            </div>
        </div>
    );
};
